import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Menu, Table, Checkbox, Grid, Input, Button, Icon, Divider, Header, Confirm, Loader, Modal } from 'semantic-ui-react';

import {
  loadVehicles,
  clearVehicles,
  loadTasks,
  searchDirections,
  vehiclesSelector,
  tasksSelector,
  directionsSelector,
  loadingSelector,
  clearDirections,
  cancelTask,
  assignTask
} from '../../ducks/rental';

import { parseDateTimeUTC } from '../../utils/dateTimeFormater';

import Field from '../../components/BaseComponents';
import DateTime from '../../components/BaseComponents/DateTime';
import Select from '../../components/BaseComponents/Select';
import StateValue from '../../components/ColumnsValue/StateValue';

import { SELECT_TYPE, TEXT_TYPE } from '../../constants/columnTypes';

import './style.scss';

const TabComponent = ({ data, onSelect, t, selectedId, columns, renderRow, additionalContent, getRowStyle }) => (
  <div className="scrollable-table-container">
    <Table celled fixed className="task-table">
      <Table.Header>
        <Table.Row>
          {columns.map((col) => (
            <Table.HeaderCell key={col}>{col}</Table.HeaderCell>
          ))}
          <Table.HeaderCell>{t('rentedTask_confirmCommentPlaceholder')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((item, i) => (
          <React.Fragment key={item.id}>
            <Table.Row style={getRowStyle ? getRowStyle(item) : {}}>
              {renderRow(item, i)}
              <Table.Cell>
                <Checkbox
                  className={`${getRowStyle ? 'custom-checkbox' : ''}`}
                  checked={selectedId === item.id}
                  onChange={() => onSelect(item)}
                />
              </Table.Cell>
            </Table.Row>
            {selectedId === item.id && additionalContent && (
              <Table.Row>
                <Table.Cell colSpan={columns.length + 1} className="collapsed-row">
                  {additionalContent}
                </Table.Cell>
              </Table.Row>
            )}
          </React.Fragment>
        ))}
      </Table.Body>
    </Table>
  </div>
);

const RentedTasks = (props) => {
  const { t } = useTranslation();
  const { location, history } = props;
  const { state } = location;
  const selectedIds = state?.selectedIds || [];

  const initialSelectionState = {
    vehicleId: null,
    taskId: null,
    directionId: null,
    shippingId: null,
  }

  const dispatch = useDispatch();
  const vehicles = useSelector(vehiclesSelector);
  const tasks = useSelector(tasksSelector);
  const directions = useSelector(directionsSelector);
  const loading = useSelector(loadingSelector);

  const [selection, setSelection] = useState(initialSelectionState);

  const [shippingCity, setShippingCity] = useState({});
  const [shippingDate, setShippingDate] = useState('');
  const [confirmation, setConfirmation] = useState({ open: false, comment: null });
  const [cancelConfirmation, setCancelConfirmation] = useState({ open: false, reason: null, carrierComment: null });

  useEffect(() => {
    dispatch(loadVehicles({ selectedIds }));

    return () => {
      dispatch(clearVehicles());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selection.vehicleId) {
      dispatch(loadTasks({ vehicleId: selection.vehicleId }));
      dispatch(clearDirections());
    }
  }, [selection.vehicleId, dispatch]);

  useEffect(() => {
    if (vehicles.length && (!selection.vehicleId || !vehicles.some(v => v.id === selection.vehicleId))) {
      setSelection((prev) => ({ ...prev, vehicleId: vehicles[0].id }));
    }
  }, [vehicles]);

  useEffect(() => {
    if (directions.length) {
      setSelection((prev) => ({
        ...prev,
        directionId: null,
        shippingId: null,
      }));
    }
  }, [directions]);
  
  const handleSelect = (type, id) => {
    setSelection((prev) => {
      if (prev[type] === id) {
        return type === 'vehicleId' ? prev : { ...prev, [type]: null };
      }
      return { ...prev, [type]: id };
    });
  };

  const handleCancelTask = () => {
    if (selection.taskId) {
      setCancelConfirmation((prev) => ({ ...prev, open: true }))
    }
  };

  const handleSearchDirections = () => {
    if (selection.vehicleId) {
      const params = {
        ...(shippingCity?.value && { shippingCity: shippingCity.value }),
        ...(shippingDate && { shippingDate: parseDateTimeUTC(shippingDate, 'DD.MM.YYYY HH:mm') }),
      };

      dispatch(clearDirections());
      dispatch(
        searchDirections({
          vehicleId: selection.vehicleId,
          params,
        })
      );
    }
  };

  const handleAssign = () => {
    if (!selection.directionId) return;
  
    const selectedDirection = directions.find(direction => direction.directionId === selection.directionId);
    if (selectedDirection && !selectedDirection.isPriority) {
      setConfirmation((prev) => ({ ...prev, open: true }))
    } else {
      dispatch(assignTask({
        vehicleId: selection.vehicleId,
        params: {
          directionId: selectedDirection.directionId,
          deliveryDate: parseDateTimeUTC(selectedShipping.deliveryDate),
          shippingDate: parseDateTimeUTC(selectedShipping.shippingDate),
          shippingId: selectedShipping.shippingId,
          shippingZoneId: selectedShipping.shippingZoneId,
          deliveryZoneId: selectedShipping.deliveryZoneId,
          comment: null,
        }
       }));
    }
  };

  const handleConfirmAssign = (comment = null) => {
    const selectedDirection = directions.find(direction => direction.directionId === selection.directionId);

    dispatch(assignTask({
      vehicleId: selection.vehicleId,
      params: {
        directionId: selectedDirection.directionId,
        deliveryDate: parseDateTimeUTC(selectedShipping.deliveryDate),
        shippingDate: parseDateTimeUTC(selectedShipping.shippingDate),
        shippingId: selectedShipping.shippingId,
        shippingZoneId: selectedShipping.shippingZoneId,
        deliveryZoneId: selectedShipping.deliveryZoneId,
        comment,
      }
    }));
    setConfirmation(false);
  };

  const handleConfirm = () => {
    if (confirmation.comment?.trim()?.length > 0) {
      handleConfirmAssign(confirmation.comment);
      setConfirmation(() => ({ open: false, comment: null }));
    } else {
      toast.error('Комментарий является обязательным полем');
    }
  };

  const handleConfirmCancelTask = () => {
    if (cancelConfirmation.reason) {
      dispatch(cancelTask({
        params: {
          shippingId: selection.taskId,
          cancellationReason: cancelConfirmation.reason.value,
          carrierComment: cancelConfirmation.carrierComment,
        },
        vehicleId: selection.vehicleId,
      }));
    
      setCancelConfirmation({ open: false, reason: null, carrierComment: null });
    } else {
      toast.error('Причина отмены является обязательным полем');
    }
  };
  

  const onClose = () => {
    setSelection(initialSelectionState);
    const gridLocation = state.pathname;
      history.replace({
          pathname: gridLocation,
          state: {
              ...state,
          },
      }); 
  };

  const formattedDirections =directions.map(direction => ({
    ...direction,
    id: direction.directionId,
    shippings: (direction.shippings || []).map(shipping => ({
      ...shipping,
      id: shipping.shippingId,
    })),
  }));

  const selectedVehicle = vehicles.find((v) => v.id === selection.vehicleId);
  const selectedShipping = formattedDirections
  .flatMap(direction => direction.shippings || [])
  .find(shipping => shipping.id === selection.shippingId);

  return (
    <>
      {loading ? (
        <Loader active={loading} size="huge">
          Loading
        </Loader>
      ) : (
      <>
        {!!selectedIds.length && (
          <div>
            <div className="card-header-panel">
              <div className="card-header-panel_title">
                <Button icon onClick={onClose}>
                  <Icon name="arrow left"/>
                </Button>
                {t('rentedTask_panelTitle', { vehicleNumber: selectedVehicle?.vehicleNumber })}
              </div>
            </div>
            <div className="card-content" style={{ height: '100vh', paddingBottom: 0}}>
              <Grid.Row className='content-container' >
                <Grid.Column width={4}>
                    <Menu text vertical className='vehicle-sidebar'>
                      <Menu.Item header className='menu-header'>{t('rentedTask_menuHeader')}</Menu.Item>
                      <Divider className='menu-divider'/>
                      {vehicles.map((vehicle) => (
                        <Menu.Item
                          key={vehicle.id}
                          onClick={() => handleSelect('vehicleId', vehicle.id)}
                          active={selection.vehicleId === vehicle.id}
                          className='menu-item '
                        >
                          {selection.vehicleId === vehicle.id && (
                            <span className="menu-item__arrow">▶</span>
                          )}
                          <span className={`menu-item__text ${
                            selection.vehicleId === vehicle.id
                              ? 'menu-item--active'
                              : 'menu-item--inactive'
                          }`}
                          >
                            {`Тягач ${vehicle.vehicleNumber}`}
                          </span>
                        </Menu.Item>
                      ))}
                    </Menu>
                  </Grid.Column>
                  <Grid.Column width={12} className='table-container'>
                    {selectedVehicle && (
                        <>
                          <div className='vehicle-info'>
                            <strong>{t('rentedTask_vehicleInfo_carrier', { carrierName: selectedVehicle?.carrierName })}</strong>
                            <strong>{t('rentedTask_vehicleInfo_phone', { driverPhone: selectedVehicle?.driverPhone })}</strong>
                            <strong>{t('rentedTask_vehicleInfo_fullName', { driverFullName: selectedVehicle?.driverFullName })}</strong>
                            <strong>{t('rentedTask_vehicleInfo_number', { vehicleNumber: selectedVehicle?.vehicleNumber })}</strong>
                            <strong>{t('rentedTask_vehicleInfo_type', { vehicleType: selectedVehicle?.vehicleType })}</strong>
                            <strong>{t('rentedTask_vehicleInfo_date', { activeShippingDate: selectedVehicle?.activeShippingDate })}</strong>
                          </div>
                          <Header className='table-header'>{t('rentedTask_assignedTasksHeader')}</Header>
                          <TabComponent
                            data={tasks}
                            selectedId={selection.taskId}
                            onSelect={(task) => handleSelect('taskId', task.id)}
                            t={t}
                            columns={[
                              t('rentedTask_column_taskNumber'), t('rentedTask_column_shippingNumber'), t('rentedTask_column_status'),
                              t('rentedTask_column_shippingCity'), t('rentedTask_column_deliveryCity'),
                              t('rentedTask_column_shippingDate'), t('rentedTask_column_deliveryDate'),
                              t('rentedTask_column_distance'), t('rentedTask_column_homeplantDistance')
                            ]}               
                            renderRow={(task, i) => (
                              <>
                                <Table.Cell>{task.number}</Table.Cell>
                                <Table.Cell>{task.shippingNumber}</Table.Cell>
                                <Table.Cell>
                                <StateValue
                                  value={task.shippingStatus}
                                  source="shippingState"
                                  indexRow={i}
                                  indexColumn={i}
                                  width={150}
                                />
                                </Table.Cell>
                                <Table.Cell>{task.shippingCity}</Table.Cell>
                                <Table.Cell>{task.deliveryCity}</Table.Cell>
                                <Table.Cell>{task.shippingDate}</Table.Cell>
                                <Table.Cell>{task.deliveryDate}</Table.Cell>
                                <Table.Cell>{task.distance}</Table.Cell>
                                <Table.Cell>{task.homeplantDistance}</Table.Cell>
                              </>
                            )}
                          />
                          <Button
                            onClick={handleCancelTask}
                            disabled={!selection.taskId}
                            className='cancel-task-button'
                          >
                            {t('rentedTask_cancelTaskButton')}
                          </Button>
                          <Grid className='search-directions'>
                            <Grid.Row>
                              <Grid.Column width={5}>
                                <Button onClick={handleSearchDirections} className='direction-button'>
                                  <p style={{ marginBottom: 0}}>
                                    {t('rentedTask_searchDirectionsButton', { tasksCount: +selectedVehicle?.tasksCount + 1})}
                                  </p>
                                  <Icon name="sync"/>
                                </Button>
                              </Grid.Column>
                              <Grid.Column width={5}>
                                <Select
                                  name="cityNames"
                                  placeholder={t('rentedTask_label_shippingCity_placeholder')}
                                  source='cityNames'
                                  text='rentedTask_label_shippingCity'
                                  value={shippingCity}
                                  onChange={(e, { value }) => setShippingCity(value)}
                                />
                              </Grid.Column>
                              <Grid.Column width={5}>
                                <DateTime
                                  name="shippingDate"
                                  text='rentedTask_label_shippingDate'
                                  value={shippingDate}
                                  onChange={(e, { value }) => setShippingDate(value)}
                                />
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>     
                          {!!directions.length && (
                            <div>
                              <Header className='table-header'>{t('rentedTask_directionsHeader')}</Header>
                              <TabComponent
                                data={formattedDirections}
                                selectedId={selection.directionId}
                                onSelect={(direction) => handleSelect('directionId', direction.id)}
                                t={t}
                                columns={[
                                  t('rentedTask_column_directionStatus'), t('rentedTask_column_directionShippingCity'),
                                  t('rentedTask_column_directionDeliveryCity'), t('rentedTask_column_shippingsCount'), t('rentedTask_column_priority')
                                ]}
                                getRowStyle={(direction) =>
                                  direction.isPriority ? { backgroundColor: '#DFFFE3' } : {}
                                }
                                renderRow={(direction, i) => (
                                  <>
                                    <Table.Cell>
                                      <StateValue
                                        value={direction.shippingStatus}
                                        source="shippingState"
                                        indexRow={i}
                                        indexColumn={i}
                                        width={150}
                                      />
                                    </Table.Cell>
                                    <Table.Cell>{direction.shippingCity}</Table.Cell>
                                    <Table.Cell>{direction.deliveryCity}</Table.Cell>
                                    <Table.Cell>{direction.shippingsCount}</Table.Cell>
                                    <Table.Cell>{direction.priority}</Table.Cell>
                                  </>
                                )}
                                additionalContent={
                                  selection.directionId && !!directions.length && directions.find(direction => direction.directionId === selection.directionId) && (
                                    <TabComponent
                                      data={formattedDirections.find(direction => direction.id === selection.directionId)?.shippings || []}
                                      selectedId={selection.shippingId}
                                      onSelect={(shipping) => handleSelect('shippingId', shipping.id)}
                                      t={t}
                                      columns={[
                                        t('rentedTask_column_shippingNumber'), t('rentedTask_column_shippingStatus'), t('rentedTask_column_vehicleType'),
                                        t('rentedTask_column_shippingCity'), t('rentedTask_column_deliveryCity'), t('rentedTask_column_shippingDate'),
                                        t('rentedTask_column_deliveryDate'), t('rentedTask_column_deliveryAddress')
                                      ]}
                                      renderRow={(shipping, i) => (
                                        <>
                                          <Table.Cell>{shipping.shippingNumber}</Table.Cell>
                                          <Table.Cell>
                                          <StateValue
                                            value={shipping.shippingStatus}
                                            source="shippingState"
                                            indexRow={i}
                                            indexColumn={i}
                                            width={150}
                                          />
                                          </Table.Cell>
                                          <Table.Cell>{shipping.vehicleType}</Table.Cell>
                                          <Table.Cell>{shipping.shippingCity}</Table.Cell>
                                          <Table.Cell>{shipping.deliveryCity}</Table.Cell>
                                          <Table.Cell>{shipping.shippingDate}</Table.Cell>
                                          <Table.Cell>{shipping.deliveryDate}</Table.Cell>
                                          <Table.Cell>{shipping.deliveryAddress}</Table.Cell>
                                        </>
                                      )}
                                    />
                                  )
                                }
                              />
                            <div className='button-container'>
                              <Button onClick={handleAssign} disabled={!selection.shippingId}>
                                {t('rentedTask_assignButton')}
                              </Button>
                            </div>
                          </div>
                          )}
                        </>
                      )}
                  </Grid.Column>
                </Grid.Row>
              </div>
              <Confirm
                closeIcon
                size='mini'
                open={confirmation.open}
                onCancel={() => setConfirmation(() => ({ open: false, comment: null }))}
                onConfirm={handleConfirm}
                confirmButton={t('rentedTask_confirmConfirmButton')}
                cancelButton={null}
                header={t('rentedTask_confirmHeader')}
                className='confirm-modal'
                content={
                  <div style={{ textAlign: 'center' }} className='confirm-content'>
                    <strong>{t('rentedTask_confirmCommentPrompt')}</strong>
                    <p>{t('rentedTask_confirmShippingNumberLabel')} <span>{selectedShipping?.shippingNumber}</span></p>
                    <Input
                      fluid
                      placeholder={t('rentedTask_confirmCommentPlaceholder')}
                      value={confirmation.comment}
                      onChange={(e, { value }) => setConfirmation((prev) => ({ ...prev, comment: value }))}
                    />
                  </div>
                }
              />
              <Modal open={cancelConfirmation.open} size='tiny'>
                  <Modal.Header>{t('rentedTask_cancelTaskConfirmTitle')}</Modal.Header>
                  <Modal.Content>
                    <div className="ui form dictionary-edit" style={{ padding: 0 }}>
                      <Grid style={{ width: '100%' }}>
                        <Grid.Row className="field-row">
                          <Grid.Column width={16}>
                            <Field
                                name="reason"
                                type={SELECT_TYPE}
                                source="cancellationReasons"
                                isRequired={true}
                                disabled={false}
                                value={cancelConfirmation.reason}
                                onChange={(e, { value }) => setCancelConfirmation(prev => ({ ...prev, reason: value }))}
                            />
                          </Grid.Column>
                          <Grid.Column width={16}>
                            <Field
                                name="carrierComment"
                                type={TEXT_TYPE}
                                disabled={false}
                                value={cancelConfirmation.carrierComment}
                                onChange={(e, { value }) => setCancelConfirmation(prev => ({ ...prev, carrierComment: value }))}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </div>
                  </Modal.Content>
                  <Modal.Actions>
                      <Button onClick={() => setCancelConfirmation({ open: false, comment: null })}>
                          {t('CancelButton')}
                      </Button>
                      <Button primary onClick={handleConfirmCancelTask}>
                          {t('Ok')}
                      </Button>
                  </Modal.Actions>
              </Modal>
          </div>
        )}
      </>
    )}
  </>
)};

export default RentedTasks;

